/**
 * Foro (1.0 admin forum moderation): «Últimos comentarios» de los usuarios (27/10, lo primero que se
 * ve), hilos por última actividad, ocultos, acciones (cerrar/ocultar/borrar/vetar) y siembra oficial.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { collection, documentId, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { env } from '../env.js';
import { db } from '../firebase.js';
import { btn, muted, Notice, str, Table, td, when } from '../lib/ui.js';

type Doc = Record<string, unknown>;
type Action =
  | 'delete_thread'
  | 'delete_post'
  | 'lock_thread'
  | 'unlock_thread'
  | 'hide_thread'
  | 'unhide_thread'
  | 'ban_user'
  | 'unban_user';

/** Últimos comentarios de usuarios en cualquier hilo, el más reciente primero. */
function LatestPosts({
  version,
  act,
}: {
  version: number;
  act: (a: Action, id: string) => void;
}): ReactElement {
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [threads, setThreads] = useState<Record<string, Doc>>({});
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setRows(null);
    const load = async (): Promise<void> => {
      const s = await getDocs(
        query(collection(db, 'forumPosts'), orderBy('created_at', 'desc'), limit(100)),
      );
      const posts = s.docs.map((d): Doc => ({ id: d.id, ...d.data() }));
      const ids = [...new Set(posts.map((p) => str(p.thread_id)).filter(Boolean))];
      const map: Record<string, Doc> = {};
      for (let i = 0; i < ids.length; i += 10) {
        const t = await getDocs(
          query(collection(db, 'forumThreads'), where(documentId(), 'in', ids.slice(i, i + 10))),
        );
        for (const d of t.docs) map[d.id] = { id: d.id, ...d.data() };
      }
      setThreads(map);
      setRows(posts);
    };
    load().catch((e: unknown) => {
      setError(errorText(e));
      setRows([]);
    });
  }, [version]);
  return (
    <>
      <Notice text={error} error />
      {!rows && <p style={muted}>Cargando…</p>}
      {rows && (
        <Table head={['Fecha', 'Autor', 'Hilo', 'Comentario', 'Acciones']}>
          {rows.map((p) => {
            const th = threads[str(p.thread_id)];
            return (
              <tr key={str(p.id)} style={{ opacity: p.status === 'hidden' ? 0.5 : 1 }}>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{when(p.created_at)}</td>
                <td style={td}>
                  <Link to={`/usuarios/${str(p.author_uid)}`}>@{str(p.author_handle)}</Link>
                </td>
                <td style={td}>
                  {th ? (
                    <a
                      href={`${env.VITE_WEB_ORIGIN}/es/forum/${str(th.category_id)}/${str(th.id)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {str(th.title)}
                    </a>
                  ) : (
                    str(p.thread_id)
                  )}
                </td>
                <td style={{ ...td, maxWidth: 520 }}>{str(p.body).slice(0, 300)}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <button
                    type="button"
                    style={btn(false, true)}
                    onClick={() => {
                      act('delete_post', str(p.id));
                    }}
                  >
                    Borrar
                  </button>{' '}
                  <button
                    type="button"
                    style={btn(false, true)}
                    onClick={() => {
                      act('ban_user', str(p.author_uid));
                    }}
                  >
                    Vetar autor
                  </button>
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td style={td} colSpan={5}>
                Aún no hay comentarios.
              </td>
            </tr>
          )}
        </Table>
      )}
    </>
  );
}

export function ForumPage(): ReactElement {
  const [status, setStatus] = useState<'comments' | 'open' | 'locked' | 'hidden'>('comments');
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    setRows(null);
    if (status === 'comments') return;
    getDocs(
      query(
        collection(db, 'forumThreads'),
        where('status', '==', status),
        orderBy('last_activity', 'desc'),
        limit(100),
      ),
    )
      .then((s) => {
        setRows(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch((e: unknown) => {
        setError(errorText(e));
        setRows([]);
      });
  }, [status, version]);
  const act = (action: Action, id: string): void => {
    if (
      (action === 'delete_thread' || action === 'delete_post' || action === 'ban_user') &&
      !window.confirm(`¿${action} ${id}?`)
    )
      return;
    setError(null);
    call(api.forum.moderate, { action, id })
      .then(() => {
        setNotice('Hecho.');
        setVersion((v) => v + 1);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  const seed = (): void => {
    call(api.forum.seed, {})
      .then((r) => {
        setNotice(
          `Siembra: ${r.categories} categorías, ${r.threads_created} hilos nuevos, ${r.threads_skipped} ya existían.`,
        );
        setVersion((v) => v + 1);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  return (
    <>
      <h1 style={{ margin: 0 }}>Foro</h1>
      <p style={muted}>
        Filtro de palabrotas y límites de frecuencia en el servidor; aquí: cerrar, ocultar, borrar
        hilos y vetar usuarios del foro. «Sembrar» crea las 12 categorías y los hilos oficiales
        (idempotente).
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {(['comments', 'open', 'locked', 'hidden'] as const).map((s) => (
          <button
            key={s}
            type="button"
            style={btn(status === s)}
            onClick={() => {
              setStatus(s);
            }}
          >
            {
              {
                comments: 'Últimos comentarios',
                open: 'Hilos abiertos',
                locked: 'Cerrados',
                hidden: 'Ocultos',
              }[s]
            }
          </button>
        ))}
        <button type="button" style={{ ...btn(false), marginLeft: 'auto' }} onClick={seed}>
          Sembrar categorías e hilos oficiales
        </button>
      </div>
      <Notice text={notice} />
      <Notice text={error} error />
      {status === 'comments' && <LatestPosts version={version} act={act} />}
      {status !== 'comments' && !rows && <p style={muted}>Cargando…</p>}
      {status !== 'comments' && rows && (
        <Table head={['Hilo', 'Categoría', 'Autor', 'Respuestas', 'Última actividad', 'Acciones']}>
          {rows.map((th) => (
            <tr key={str(th.id)}>
              <td style={td}>
                <a
                  href={`${env.VITE_WEB_ORIGIN}/es/forum/${str(th.category_id)}/${str(th.id)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {str(th.title)}
                </a>
              </td>
              <td style={td}>{str(th.category_id)}</td>
              <td style={td}>
                <Link to={`/usuarios/${str(th.author_uid)}`}>@{str(th.author_handle)}</Link>
              </td>
              <td style={td}>{str(th.reply_count)}</td>
              <td style={td}>{when(th.last_activity)}</td>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>
                {th.status === 'locked' ? (
                  <button
                    type="button"
                    style={btn(false)}
                    onClick={() => {
                      act('unlock_thread', str(th.id));
                    }}
                  >
                    Reabrir
                  </button>
                ) : (
                  <button
                    type="button"
                    style={btn(false)}
                    onClick={() => {
                      act('lock_thread', str(th.id));
                    }}
                  >
                    Cerrar
                  </button>
                )}{' '}
                {th.status === 'hidden' ? (
                  <button
                    type="button"
                    style={btn(false)}
                    onClick={() => {
                      act('unhide_thread', str(th.id));
                    }}
                  >
                    Mostrar
                  </button>
                ) : (
                  <button
                    type="button"
                    style={btn(false)}
                    onClick={() => {
                      act('hide_thread', str(th.id));
                    }}
                  >
                    Ocultar
                  </button>
                )}{' '}
                <button
                  type="button"
                  style={btn(false, true)}
                  onClick={() => {
                    act('delete_thread', str(th.id));
                  }}
                >
                  Borrar
                </button>{' '}
                <button
                  type="button"
                  style={btn(false, true)}
                  onClick={() => {
                    act('ban_user', str(th.author_uid));
                  }}
                >
                  Vetar autor
                </button>{' '}
                <button
                  type="button"
                  style={btn(false)}
                  onClick={() => {
                    act('unban_user', str(th.author_uid));
                  }}
                >
                  Quitar veto
                </button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td style={td} colSpan={6}>
                Nada.
              </td>
            </tr>
          )}
        </Table>
      )}
    </>
  );
}
